import { Form, Formik, Field, ErrorMessage } from 'formik';
import styles from './RequestOTP.module.css';
import { Helmet } from 'react-helmet';
import FormField from 'ui/DataEntry/FormField/FormField';
import Label from 'ui/DataEntry/Label/Label';
import Button from 'ui/Buttons/Button/Button';
import { allAuth, encodeUrlSearchParams } from '@round/api';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from 'Modules/Auth/contexts/AuthContext';

const RequestOTP = () => {
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const location = useLocation();
    const nextUrl = new URLSearchParams(location.search).get('next') ?? '/';
    const state = location.state || {};
    const email = (state as { email?: string }).email;

    if (authContext.authenticated) {
        return <Navigate to={nextUrl} replace />;
    }

    return (
        <>
            <Helmet title="Sign in via email | round" />
            <div className={styles.container}>
                <h1 className={styles.heading}>Sign in</h1>
                <Formik
                    initialValues={{ email: email ?? '' }}
                    onSubmit={async (values, { setFieldError }) => {
                        try {
                            const response = await allAuth.requestCode(values.email);
                            if (response.status === 401) {
                                navigate(
                                    {
                                        pathname: '/login/otp/confirm/',
                                        search: encodeUrlSearchParams({ next: nextUrl }),
                                    },
                                    {
                                        state: { sessionToken: response.meta.session_token, email: values.email },
                                    }
                                );
                                return;
                            }

                            setFieldError('email', response.errors[0]?.message ?? 'Could not request code');
                        } catch {
                            setFieldError('email', 'Could not request code');
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <FormField>
                                <Label htmlFor="email">Email</Label>
                                <Field
                                    tabIndex={1}
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    className={styles.input}
                                />
                                <ErrorMessage name="email">
                                    {(message) => <span className={styles.error}>{message}</span>}
                                </ErrorMessage>
                            </FormField>
                            <Button
                                tabIndex={2}
                                loading={isSubmitting}
                                className={styles.submit}
                                htmlType="submit"
                                type="filled"
                                color="black"
                            >
                                Send code
                            </Button>
                            <p className={styles.helpText}>
                                Enter your email to get started. We'll send you a login code.
                            </p>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default RequestOTP;
